import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { IconArrowRight, IconEye, IconEyeOff } from '@tabler/icons-react';
import { Link } from '@planne-software/uni';
import { Button } from '@planne-software/uni/Button';
import { Input } from '@planne-software/uni/Input';

import { useTranslate } from '@src/core/hooks';
import {
  AuthCardButtonWrapper,
  AuthCardRoot,
  AuthCardSubtitle,
  AuthCardTextGroup,
  AuthCardTitle,
  authLinkSx,
  ButtonGroup,
  PasswordSuffixButton,
  RecoveryLinkPrefix,
  RecoveryLinkRow,
  SignupLinkWrapper,
} from '@src/features/customers';
import { AuthMethodDivider } from '@src/features/customers/auth/identification';

import { usePasswordLogin } from '../../hooks/usePasswordLogin';
import { PasswordFieldStack } from './styles';

type PasswordLoginCardProps = {
  initialEmail: string;
  onSwitchToOtp: () => void;
  onForgotPassword: () => void;
  onSignup: (email: string) => void;
  onEmailChange?: (email: string) => void;
  onLoggedIn?: () => void;
  submitLabel?: string;
  subtitle?: string;
  signupLabel?: string;
};

export const PasswordLoginCard = (props: PasswordLoginCardProps) => {
  const { initialEmail, onSwitchToOtp, onForgotPassword, onSignup, onEmailChange, onLoggedIn } = props;
  const { submitLabel, subtitle, signupLabel } = props;
  const { i18n } = useTranslate();
  const [passwordVisible, setPasswordVisible] = useState(false);

  const { control, errors, loading, isSubmitEnabled, isEmailValid, email, onSubmit, onEmailInputChange } =
    usePasswordLogin({
      initialEmail,
      onEmailChange,
      onLoggedIn,
    });

  return (
    <AuthCardRoot data-testid='password-login-card'>
      <AuthCardTextGroup>
        <AuthCardTitle>{i18n('checkout_auth_login_title')}</AuthCardTitle>
        <AuthCardSubtitle>{subtitle ?? i18n('auth_login_password_subtitle')}</AuthCardSubtitle>
      </AuthCardTextGroup>

      <form onSubmit={onSubmit} noValidate>
        <AuthCardRoot>
          <Controller
            name='email'
            control={control}
            render={({ field: { value, name, ref, ...rest } }) => (
              <Input
                type='email'
                label={i18n('checkout_auth_email_label')}
                name={name}
                id={name}
                value={value ?? ''}
                error={!!errors.email}
                disabled={loading}
                helperText={errors.email?.message}
                autoComplete='email'
                ref={ref}
                size='medium'
                {...rest}
                onChange={(event) => onEmailInputChange(event.target.value)}
              />
            )}
          />

          <PasswordFieldStack>
            <Controller
              name='password'
              control={control}
              render={({ field: { onChange, value, name, ref, ...rest } }) => (
                <Input
                  type={passwordVisible ? 'text' : 'password'}
                  label={i18n('checkout_auth_password_label')}
                  name={name}
                  id={name}
                  value={value ?? ''}
                  onChange={onChange}
                  error={!!errors.password}
                  disabled={loading}
                  helperText={errors.password?.message}
                  autoComplete='current-password'
                  ref={ref}
                  size='medium'
                  suffixSlot={
                    <PasswordSuffixButton
                      type='button'
                      onClick={() => setPasswordVisible((prev) => !prev)}
                      aria-label={
                        passwordVisible ? i18n('checkout_auth_hide_password') : i18n('checkout_auth_show_password')
                      }
                    >
                      {passwordVisible ? <IconEyeOff size={20} aria-hidden /> : <IconEye size={20} aria-hidden />}
                    </PasswordSuffixButton>
                  }
                  {...rest}
                />
              )}
            />

            <RecoveryLinkRow>
              <RecoveryLinkPrefix>{i18n('checkout_auth_forgot_password_prefix')}</RecoveryLinkPrefix>
              <Link
                component='button'
                type='button'
                underline='none'
                onClick={onForgotPassword}
                disabled={loading}
                sx={authLinkSx}
              >
                {i18n('checkout_auth_forgot_password_link')}
              </Link>
            </RecoveryLinkRow>
          </PasswordFieldStack>

          <ButtonGroup>
            <AuthCardButtonWrapper>
              <Button
                type='submit'
                fullWidth
                loading={loading}
                disabled={!isSubmitEnabled}
                endSlot={<IconArrowRight aria-hidden />}
              >
                {submitLabel ?? i18n('auth_login_submit_cta')}
              </Button>
            </AuthCardButtonWrapper>

            <AuthCardButtonWrapper>
              <Button
                type='button'
                variant='outlined'
                fullWidth
                disabled={!isEmailValid || loading}
                onClick={onSwitchToOtp}
                endSlot={<IconArrowRight aria-hidden />}
              >
                {i18n('checkout_auth_login_with_code_cta')}
              </Button>
            </AuthCardButtonWrapper>
          </ButtonGroup>
        </AuthCardRoot>
      </form>

      <AuthMethodDivider />

      <SignupLinkWrapper>
        <Link
          component='button'
          type='button'
          underline='none'
          onClick={() => onSignup(email)}
          disabled={loading}
          sx={authLinkSx}
        >
          {signupLabel ?? i18n('auth_login_signup_link')}
        </Link>
      </SignupLinkWrapper>
    </AuthCardRoot>
  );
};

export default PasswordLoginCard;
